// client/src/coms/personalisation/prefsSchema.js
// Client copy of server/utils/personalisationSchema.js (generated from it; the server copy is authoritative and
// server/test/personalisation.test.js checks they agree). Used for defaults, live previews and import checks.
// Authoritative validation for Voxemera personalisation documents (account prefs, per-server overrides,
// workspace tabs, themes). Everything a client sends is rebuilt from an allowlist: unknown keys are dropped,
// enums must match exactly, numbers are clamped, colours must be plain hex. A theme is pure data: it never
// carries CSS, URLs or markup, so nothing in it can execute or load a resource.
//
export const SCHEMA_VERSION = 1;
export const LIMITS = {
  prefsBytes: 16 * 1024,
  serverPrefsBytes: 8 * 1024,
  workspaceBytes: 16 * 1024,
  themeBytes: 4 * 1024,
  themePresetsPerUser: 30,
  tabs: 30,
  closedTabs: 10,
  nameChars: 40,
};

export const LAYOUT_PRESETS = ['classic', 'compact', 'focus', 'social', 'creator', 'custom'];
export const THEME_MODES = ['dark', 'light', 'oled', 'contrast', 'system', 'custom'];
export const THEME_COLOR_KEYS = ['accent', 'background', 'surface', 'panel', 'sidebar', 'text', 'textMuted', 'border', 'messageBg', 'link', 'hover', 'selected'];

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const SERVER_KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;
const CHANNEL_ID_RE = /^(?:text|voice):\d{1,12}$/;
const THREAD_ID_RE = /^\d{1,12}$/;

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function pickEnum(v, allowed, fallback) {
  return allowed.includes(v) ? v : fallback;
}

function pickBool(v, fallback) {
  return typeof v === 'boolean' ? v : fallback;
}

function pickNum(v, min, max, fallback, step = 0) {
  const n = typeof v === 'number' ? v : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  let out = Math.min(max, Math.max(min, n));
  if (step) out = Math.round(out / step) * step;
  return Number(out.toFixed(4));
}

export function normHex(v) {
  if (typeof v !== 'string' || !HEX_RE.test(v.trim())) return null;
  let s = v.trim().toLowerCase();
  if (s.length === 4) s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return s;
}

export function cleanName(v, fallback = '') {
  if (typeof v !== 'string') return fallback;
  // Printable text only: no control characters or angle brackets (names are shown as text, never markup).
  // eslint-disable-next-line no-control-regex
  const s = v.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, LIMITS.nameChars);
  return s || fallback;
}

export function byteLength(value) {
  // UTF-8 length (the same number as Buffer.byteLength on the server).
  let n = 0;
  for (const ch of JSON.stringify(value == null ? null : value)) {
    const c = ch.codePointAt(0);
    n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }
  return n;
}

// ---------- colour contrast (WCAG 2.x) ----------
function hexToRgb(hex) {
  const h = normHex(hex);
  if (!h) return null;
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

function luminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// ---------- themes ----------
// A theme: { v, name, base: 'dark'|'light', colors: {<key>: '#rrggbb'}, gradient: {type, angle, stops} | null,
// contrast: 'normal'|'high' }. Missing colours fall back to the base theme on the client.
export function sanitizeTheme(input) {
  if (!isObj(input)) return null;
  const colors = {};
  const src = isObj(input.colors) ? input.colors : {};
  for (const key of THEME_COLOR_KEYS) {
    const hex = normHex(src[key]);
    if (hex) colors[key] = hex;
  }
  let gradient = null;
  if (isObj(input.gradient) && ['linear', 'radial'].includes(input.gradient.type)) {
    const stops = (Array.isArray(input.gradient.stops) ? input.gradient.stops : []).map(normHex).filter(Boolean).slice(0, 3);
    if (stops.length >= 2) {
      gradient = { type: input.gradient.type, angle: pickNum(input.gradient.angle, 0, 360, 135, 1), stops };
    }
  }
  return {
    v: SCHEMA_VERSION,
    name: cleanName(input.name, 'Custom theme'),
    base: pickEnum(input.base, ['dark', 'light'], 'dark'),
    colors,
    gradient,
    contrast: pickEnum(input.contrast, ['normal', 'high'], 'normal'),
  };
}

// Strict check used for imports and server themes: reports every problem instead of silently fixing it.
export function validateThemeStrict(input) {
  const errors = [];
  if (!isObj(input)) return { ok: false, errors: ['Theme must be an object'] };
  const allowedTop = new Set(['v', 'name', 'base', 'colors', 'gradient', 'contrast']);
  for (const k of Object.keys(input)) if (!allowedTop.has(k)) errors.push(`Unknown field "${k}"`);
  if (input.v != null && input.v !== SCHEMA_VERSION) errors.push(`Unsupported theme version ${JSON.stringify(input.v)}`);
  if (input.name != null && typeof input.name !== 'string') errors.push('name must be text');
  if (typeof input.name === 'string' && input.name.length > LIMITS.nameChars) errors.push(`name is longer than ${LIMITS.nameChars} characters`);
  if (input.base != null && !['dark', 'light'].includes(input.base)) errors.push('base must be "dark" or "light"');
  if (input.contrast != null && !['normal', 'high'].includes(input.contrast)) errors.push('contrast must be "normal" or "high"');
  if (input.colors != null) {
    if (!isObj(input.colors)) errors.push('colors must be an object');
    else {
      for (const [k, v] of Object.entries(input.colors)) {
        if (!THEME_COLOR_KEYS.includes(k)) errors.push(`Unknown colour "${k}"`);
        else if (!normHex(v)) errors.push(`Colour "${k}" must be a hex value like #5865f2`);
      }
    }
  }
  if (input.gradient != null) {
    const g = input.gradient;
    if (!isObj(g)) errors.push('gradient must be an object or null');
    else {
      for (const k of Object.keys(g)) if (!['type', 'angle', 'stops'].includes(k)) errors.push(`Unknown gradient field "${k}"`);
      if (!['linear', 'radial'].includes(g.type)) errors.push('gradient.type must be "linear" or "radial"');
      if (g.angle != null && (typeof g.angle !== 'number' || g.angle < 0 || g.angle > 360)) errors.push('gradient.angle must be 0-360');
      if (!Array.isArray(g.stops) || g.stops.length < 2 || g.stops.length > 3 || !g.stops.every((s) => normHex(s))) {
        errors.push('gradient.stops must be 2-3 hex colours');
      }
    }
  }
  if (!errors.length && byteLength(input) > LIMITS.themeBytes) errors.push('Theme is too large');
  if (errors.length) return { ok: false, errors };
  const theme = sanitizeTheme(input);
  const access = themeAccessibility(theme);
  return { ok: true, errors: [], theme, accessibility: access };
}

const BASE_COLORS = {
  dark: { background: '#1e1f22', surface: '#313338', panel: '#2b2d31', text: '#ffffff', textMuted: '#b5bac1', accent: '#5865f2', link: '#8db4ff' },
  light: { background: '#e3e5e8', surface: '#ffffff', panel: '#f2f3f5', text: '#1f2328', textMuted: '#4e5058', accent: '#4752c4', link: '#2456c7' },
};

// Minimum readability: body text >= 4.5:1 and muted text >= 3:1 on the main surface and panels.
export function themeAccessibility(theme) {
  const t = theme && isObj(theme) ? theme : {};
  const base = BASE_COLORS[t.base === 'light' ? 'light' : 'dark'];
  const c = { ...base, ...(isObj(t.colors) ? t.colors : {}) };
  const checks = [
    { pair: 'text on surface', ratio: contrastRatio(c.text, c.surface), min: 4.5 },
    { pair: 'text on panel', ratio: contrastRatio(c.text, c.sidebar || c.panel), min: 4.5 },
    { pair: 'text on background', ratio: contrastRatio(c.text, c.background), min: 4.5 },
    { pair: 'muted text on surface', ratio: contrastRatio(c.textMuted, c.surface), min: 3 },
    { pair: 'link on surface', ratio: contrastRatio(c.link, c.surface), min: 3 },
  ];
  if (c.messageBg) checks.push({ pair: 'text on messages', ratio: contrastRatio(c.text, c.messageBg), min: 4.5 });
  const failures = checks.filter((x) => x.ratio < x.min).map((x) => ({ ...x, ratio: Number(x.ratio.toFixed(2)) }));
  return { ok: failures.length === 0, failures };
}

// ---------- account prefs ----------
export const DEFAULT_PREFS = {
  v: SCHEMA_VERSION,
  layout: {
    preset: 'classic',
    sidebar: { visible: true, side: 'left', width: 290 },
    members: { visible: true, side: 'right', width: 240 },
    userBar: 'bottom',
    tabBar: 'auto',
    headerBar: true,
    activitiesButton: true,
    activityPlacement: 'main',
    density: 'comfortable',
    chatDensity: 'cozy',
    messageScale: 1,
    uiScale: 1,
    split: { mode: 'right', ratio: 0.5 },
  },
  theme: { mode: 'dark', customId: null, custom: null, accent: null, reduceMotion: 'system', serverThemes: 'ask', speakingMotion: 'follow', focusRing: 'standard' },
  tabs: { restore: true },
  mobile: {
    preset: 'classic',
    nav: ['chats', 'servers', 'dms', 'activities', 'more'],
    navPosition: 'bottom',
    navStyle: 'icons-labels',
    navBehaviour: 'always',
    navSize: 'regular',
    navOverflow: 'more',
    headerSize: 'regular',
    voiceDock: 'always',
    tabBar: 'auto',
    headerBar: true,
    density: 'comfortable',
    chatDensity: 'cozy',
    gestures: { swipeRight: 'servers', swipeLeft: 'members', swipeUp: 'none', swipeDown: 'none' },
    messageScale: 1,
    swipe: true,
    saved: [],
  },
};

export function sanitizeLayout(input) {
  const src = isObj(input) ? input : {};
  const d = DEFAULT_PREFS.layout;
  const sb = isObj(src.sidebar) ? src.sidebar : {};
  const mb = isObj(src.members) ? src.members : {};
  const sp = isObj(src.split) ? src.split : {};
  return {
    preset: pickEnum(src.preset, LAYOUT_PRESETS, d.preset),
    sidebar: {
      visible: pickBool(sb.visible, d.sidebar.visible),
      side: pickEnum(sb.side, ['left', 'right'], d.sidebar.side),
      width: pickNum(sb.width, 260, 560, d.sidebar.width, 1),
    },
    members: {
      visible: pickBool(mb.visible, d.members.visible),
      side: pickEnum(mb.side, ['right', 'left'], d.members.side),
      width: pickNum(mb.width, 200, 420, d.members.width, 1),
    },
    userBar: pickEnum(src.userBar, ['bottom', 'top'], d.userBar),
    tabBar: pickEnum(src.tabBar, ['auto', 'always', 'never'], d.tabBar),
    headerBar: pickBool(src.headerBar, d.headerBar),
    activitiesButton: pickBool(src.activitiesButton, d.activitiesButton),
    // 'beside-chat': while an activity (Activities, Canvas, Watch) fills the stage, the conversation stays open beside it.
    activityPlacement: pickEnum(src.activityPlacement, ['main', 'beside-chat'], d.activityPlacement),
    density: pickEnum(src.density, ['comfortable', 'compact'], d.density),
    chatDensity: pickEnum(src.chatDensity, ['cozy', 'compact'], d.chatDensity),
    messageScale: pickNum(src.messageScale, 0.8, 1.5, d.messageScale, 0.05),
    uiScale: pickNum(src.uiScale, 0.8, 1.3, d.uiScale, 0.05),
    split: {
      mode: pickEnum(sp.mode, ['right', 'down'], d.split.mode),
      ratio: pickNum(sp.ratio, 0.2, 0.8, d.split.ratio, 0.01),
    },
  };
}

function sanitizeThemePrefs(input) {
  const src = isObj(input) ? input : {};
  const d = DEFAULT_PREFS.theme;
  const customId = Number.isInteger(src.customId) && src.customId > 0 ? src.customId : null;
  return {
    mode: pickEnum(src.mode, THEME_MODES, d.mode),
    customId,
    custom: src.custom == null ? null : sanitizeTheme(src.custom),
    accent: normHex(src.accent),
    reduceMotion: pickEnum(src.reduceMotion, ['system', 'on', 'off'], d.reduceMotion),
    serverThemes: pickEnum(src.serverThemes, ['ask', 'never'], d.serverThemes),
    // Speaking animations with reduced motion on: 'follow' shows them as still badges, 'always' animates them.
    speakingMotion: pickEnum(src.speakingMotion, ['follow', 'always'], d.speakingMotion),
    focusRing: pickEnum(src.focusRing, ['standard', 'strong'], d.focusRing),
  };
}


// ---------- mobile layout (phones; independent of the desktop layout above) ----------
// Destinations phone navigation can show, in the user's order.
export const MOBILE_NAV_IDS = ['chats', 'servers', 'dms', 'activities', 'chat', 'watch', 'canvas', 'studio', 'maps', 'voice', 'files', 'search', 'members', 'marketplace', 'profile', 'settings', 'more'];
// Always present, so navigation and Settings can never be hidden: More (everything else, including Settings and
// Mobile Layout) stays in every bar and menu.
export const MOBILE_NAV_LOCKED = ['more'];
// 'social' and 'creator' were presets before the layouts below; saved values load as Custom.
export const MOBILE_PRESETS = ['classic', 'compact', 'top', 'bottom', 'dropdown', 'gesture', 'custom'];
export const MOBILE_NAV_POSITIONS = ['bottom', 'top', 'dropdown-top', 'dropdown-bottom', 'gesture'];
export const MOBILE_NAV_STYLES = ['icons-labels', 'icons', 'labels', 'emoji', 'emoji-labels'];
export const MOBILE_NAV_BEHAVIOURS = ['always', 'autohide', 'compact'];
export const MOBILE_SIZES = ['compact', 'regular', 'large'];
export const MOBILE_HEADER_SIZES = ['slim', 'regular', 'large'];
export const MOBILE_GESTURE_DIRECTIONS = ['swipeRight', 'swipeLeft', 'swipeUp', 'swipeDown'];
export const MOBILE_GESTURE_ACTIONS = ['none', 'servers', 'members', 'chats', 'switcher', 'menu', 'prev', 'next'];
const MOBILE_NAV_MIN = 2;
const MOBILE_NAV_MAX = 12;
const MOBILE_SAVED_MAX = 5;

function sanitizeMobileNav(v, fallback) {
  if (!Array.isArray(v)) return fallback.slice();
  const out = [];
  for (const id of v) if (MOBILE_NAV_IDS.includes(id) && !out.includes(id)) out.push(id);
  if (!out.includes('more')) out.push('more');
  if (out.length > MOBILE_NAV_MAX) return [...out.filter((id) => id !== 'more').slice(0, MOBILE_NAV_MAX - 1), 'more'];
  return out.length >= MOBILE_NAV_MIN ? out : fallback.slice();
}

// One action per gesture, and no action on two gestures (two ways of doing the same thing by accident).
function sanitizeGestures(v, fallback) {
  const src = isObj(v) ? v : {};
  const out = {};
  const used = new Set();
  for (const dir of MOBILE_GESTURE_DIRECTIONS) {
    let a = pickEnum(src[dir], MOBILE_GESTURE_ACTIONS, isObj(v) ? 'none' : fallback[dir]);
    if (a !== 'none' && used.has(a)) a = 'none';
    if (a !== 'none') used.add(a);
    out[dir] = a;
  }
  return out;
}

function sanitizeMobileLayoutValues(src, d) {
  // Saved before navigation styles existed: labels on or off maps to app icons with or without labels.
  const legacyStyle = src.navLabels === false ? 'icons' : src.navLabels === true ? 'icons-labels' : d.navStyle;
  const legacyCompact = src.preset === 'compact' && src.navStyle === undefined;
  return {
    nav: sanitizeMobileNav(src.nav, d.nav),
    navPosition: pickEnum(src.navPosition, MOBILE_NAV_POSITIONS, d.navPosition),
    navStyle: pickEnum(src.navStyle, MOBILE_NAV_STYLES, legacyStyle),
    navBehaviour: pickEnum(src.navBehaviour, MOBILE_NAV_BEHAVIOURS, d.navBehaviour),
    navSize: pickEnum(src.navSize, MOBILE_SIZES, legacyCompact ? 'compact' : d.navSize),
    navOverflow: pickEnum(src.navOverflow, ['more', 'scroll'], d.navOverflow),
    headerSize: pickEnum(src.headerSize, MOBILE_HEADER_SIZES, legacyCompact ? 'slim' : d.headerSize),
    // Voice controls while connected: a dock above the content on every screen, or only in the channel drawer.
    voiceDock: pickEnum(src.voiceDock, ['always', 'drawer'], d.voiceDock),
    tabBar: pickEnum(src.tabBar, ['auto', 'always', 'never'], d.tabBar),
    headerBar: pickBool(src.headerBar, d.headerBar),
    density: pickEnum(src.density, ['comfortable', 'compact'], d.density),
    chatDensity: pickEnum(src.chatDensity, ['cozy', 'compact'], d.chatDensity),
    gestures: sanitizeGestures(src.gestures, d.gestures),
  };
}

function sanitizeSavedLayouts(v) {
  if (!Array.isArray(v)) return [];
  const out = [];
  for (const s of v) {
    if (!isObj(s) || !isObj(s.layout)) continue;
    const name = String(s.name || '').replace(/\s+/g, ' ').trim().slice(0, 30);
    if (!name || out.some((o) => o.name === name)) continue;
    out.push({ name, layout: sanitizeMobileLayoutValues(s.layout, DEFAULT_PREFS.mobile) });
    if (out.length >= MOBILE_SAVED_MAX) break;
  }
  return out;
}

export function sanitizeMobile(input) {
  const src = isObj(input) ? input : {};
  const d = DEFAULT_PREFS.mobile;
  return {
    preset: pickEnum(src.preset, MOBILE_PRESETS, src.preset === 'social' || src.preset === 'creator' ? 'custom' : d.preset),
    ...sanitizeMobileLayoutValues(src, d),
    messageScale: pickNum(src.messageScale, 0.8, 1.5, d.messageScale, 0.05),
    // Master switch for every swipe gesture (the gestures themselves are above).
    swipe: pickBool(src.swipe, d.swipe),
    saved: sanitizeSavedLayouts(src.saved),
  };
}

export function sanitizePrefs(input) {
  const src = isObj(input) ? input : {};
  return {
    v: SCHEMA_VERSION,
    layout: sanitizeLayout(src.layout),
    theme: sanitizeThemePrefs(src.theme),
    tabs: { restore: pickBool(isObj(src.tabs) ? src.tabs.restore : undefined, DEFAULT_PREFS.tabs.restore) },
    mobile: sanitizeMobile(src.mobile),
  };
}

// ---------- per-server overrides (flat "dot.path" -> value; only what the user customised) ----------
export const SERVER_OVERRIDE_KEYS = {
  'layout.preset': (v) => (LAYOUT_PRESETS.includes(v) && v !== 'custom' ? v : undefined),
  'layout.sidebar.visible': (v) => (typeof v === 'boolean' ? v : undefined),
  'layout.members.visible': (v) => (typeof v === 'boolean' ? v : undefined),
  'layout.density': (v) => (['comfortable', 'compact'].includes(v) ? v : undefined),
  'layout.chatDensity': (v) => (['cozy', 'compact'].includes(v) ? v : undefined),
  'layout.tabBar': (v) => (['auto', 'always', 'never'].includes(v) ? v : undefined),
  'layout.headerBar': (v) => (typeof v === 'boolean' ? v : undefined),
  // 'global' = my theme, 'server' = the theme this server suggests, or a built-in / saved theme for here.
  'theme.choice': (v) =>
    typeof v === 'string' && (['global', 'server', 'dark', 'light', 'oled', 'contrast'].includes(v) || /^custom:\d{1,12}$/.test(v)) ? v : undefined,
  'nav.landingChannel': (v) => (typeof v === 'string' && (v === '' || CHANNEL_ID_RE.test(v)) ? v : undefined),
  'layout.activityPlacement': (v) => (['main', 'beside-chat'].includes(v) ? v : undefined),
  'layout.activitiesButton': (v) => (typeof v === 'boolean' ? v : undefined),
  'notifications.badges': (v) => (['all', 'mentions', 'none'].includes(v) ? v : undefined),
  // Set once the user answered this server's theme suggestion, so it is not offered again.
  'theme.suggestionSeen': (v) => (typeof v === 'boolean' ? v : undefined),
};

export function sanitizeServerOverrides(input) {
  const src = isObj(input) ? input : {};
  const out = {};
  for (const [key, check] of Object.entries(SERVER_OVERRIDE_KEYS)) {
    if (!Object.prototype.hasOwnProperty.call(src, key)) continue;
    const v = check(src[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

// ---------- workspace (conversation tabs + split) ----------
export function tabIdOf(tab) {
  if (!tab) return '';
  return tab.k === 'c' ? `c:${tab.s}:${tab.c}` : tab.k === 'd' ? `d:${tab.t}` : '';
}

export function sanitizeTab(input) {
  if (!isObj(input)) return null;
  const pinned = input.p === true || input.p === 1;
  if (input.k === 'c') {
    const s = typeof input.s === 'string' ? input.s.trim() : '';
    const c = typeof input.c === 'string' ? input.c.trim() : '';
    if (!SERVER_KEY_RE.test(s) || !CHANNEL_ID_RE.test(c)) return null;
    return { k: 'c', s, c, p: pinned };
  }
  if (input.k === 'd') {
    const t = String(input.t == null ? '' : input.t).trim();
    if (!THREAD_ID_RE.test(t)) return null;
    return { k: 'd', t, p: pinned };
  }
  return null;
}

export function sanitizeWorkspace(input) {
  const src = isObj(input) ? input : {};
  const seen = new Set();
  const tabs = [];
  for (const raw of Array.isArray(src.tabs) ? src.tabs : []) {
    if (tabs.length >= LIMITS.tabs) break;
    const tab = sanitizeTab(raw);
    if (!tab) continue;
    const id = tabIdOf(tab);
    if (seen.has(id)) continue;
    seen.add(id);
    tabs.push(tab);
  }
  const active = typeof src.active === 'string' && seen.has(src.active) ? src.active : tabs.length ? tabIdOf(tabs[0]) : null;
  const sp = isObj(src.split) ? src.split : {};
  const secondary = typeof sp.secondary === 'string' && seen.has(sp.secondary) && sp.secondary !== active ? sp.secondary : null;
  // The one temporary "preview" tab (browsing replaces it); never pinned, never the split pane.
  const previewTab = typeof src.preview === 'string' && seen.has(src.preview) ? tabs.find((t) => tabIdOf(t) === src.preview) : null;
  const preview = previewTab && !previewTab.p && src.preview !== secondary ? src.preview : null;
  const closed = [];
  for (const raw of Array.isArray(src.closed) ? src.closed : []) {
    if (closed.length >= LIMITS.closedTabs) break;
    const tab = sanitizeTab(raw);
    if (tab) closed.push(tab);
  }
  return {
    v: SCHEMA_VERSION,
    tabs,
    active,
    preview,
    split: { open: sp.open === true && !!secondary, secondary, swapped: sp.swapped === true },
    closed,
  };
}
